import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

interface NuestraCancionProps {
  myTracks: any[];
  herTracks: any[];
  myProfile: any;
  herProfile: any;
}

interface CompatibleTrack {
  track: any;
  compatibilityScore: number;
  reason: string;
}

export const NuestraCancion: React.FC<NuestraCancionProps> = ({
  myTracks,
  herTracks,
  myProfile,
  herProfile
}) => {
  const [ourSong, setOurSong] = useState<CompatibleTrack | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [top3Songs, setTop3Songs] = useState<CompatibleTrack[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [lastRefreshTime, setLastRefreshTime] = useState<number>(0);
  const [cooldownRemaining, setCooldownRemaining] = useState<number>(0);

  const COOLDOWN_SECONDS = 30; // 30 second cooldown

  // Algoritmo para encontrar "nuestra canción" - Save top 3 shared songs
  const findOurSong = () => {
    setIsCalculating(true);

    setTimeout(() => {
      const compatibleTracks: CompatibleTrack[] = [];

      // Buscar coincidencias directas (misma canción) - PRIORITIZE SHARED SONGS
      myTracks.forEach(myTrack => {
        const matchingTrack = herTracks.find(herTrack =>
          myTrack.id === herTrack.id ||
          (myTrack.name.toLowerCase() === herTrack.name.toLowerCase() &&
           myTrack.artists[0].name.toLowerCase() === herTrack.artists[0].name.toLowerCase())
        );

        if (matchingTrack) {
          compatibleTracks.push({
            track: myTrack,
            compatibilityScore: 100,
            reason: "¡Es el top favorito de ambos!"
          });
        }
      });

      // Si no hay coincidencias directas, buscar por artista
      if (compatibleTracks.length === 0) {
        const myArtists = myTracks.flatMap(track => track.artists.map((a: any) => a.name.toLowerCase()));
        const herArtists = herTracks.flatMap(track => track.artists.map((a: any) => a.name.toLowerCase()));

        myTracks.forEach(myTrack => {
          const artistName = myTrack.artists[0].name.toLowerCase();
          if (herArtists.includes(artistName)) {
            compatibleTracks.push({
              track: myTrack,
              compatibilityScore: 80,
              reason: `Ambos aman a ${myTrack.artists[0].name}`
            });
          }
        });
      }

      // Si no hay coincidencias por artista, tomar la canción más popular
      if (compatibleTracks.length === 0) {
        const allTracks = [...myTracks, ...herTracks];
        const mostPopular = allTracks.reduce((prev, current) =>
          (prev.popularity > current.popularity) ? prev : current
        );

        compatibleTracks.push({
          track: mostPopular,
          compatibilityScore: 60,
          reason: "La canción más popular de ambos"
        });
      }

      // Sort by compatibility and take top 3
      const sortedTracks = compatibleTracks
        .sort((a, b) => b.compatibilityScore - a.compatibilityScore)
        .slice(0, 3);

      setTop3Songs(sortedTracks);

      // Save to localStorage
      localStorage.setItem('ourSongTop3', JSON.stringify(sortedTracks));
      localStorage.setItem('ourSongIndex', '0');

      setCurrentIndex(0);
      setOurSong(sortedTracks[0]);
      setIsCalculating(false);

      // Update last refresh time
      const now = Date.now();
      setLastRefreshTime(now);
      localStorage.setItem('ourSongLastRefresh', now.toString());
    }, 2000);
  };

  // Load saved songs on mount
  useEffect(() => {
    const savedTop3 = localStorage.getItem('ourSongTop3');
    const savedIndex = localStorage.getItem('ourSongIndex');
    const savedLastRefresh = localStorage.getItem('ourSongLastRefresh');

    if (savedLastRefresh) {
      setLastRefreshTime(parseInt(savedLastRefresh));
    }

    if (savedTop3 && myTracks.length > 0 && herTracks.length > 0) {
      const parsedTop3 = JSON.parse(savedTop3);
      const parsedIndex = savedIndex ? parseInt(savedIndex) : 0;
      setTop3Songs(parsedTop3);
      setCurrentIndex(parsedIndex);
      setOurSong(parsedTop3[parsedIndex]);
    } else if (myTracks.length > 0 && herTracks.length > 0) {
      findOurSong();
    }
  }, [myTracks, herTracks]);

  // Cooldown timer effect
  useEffect(() => {
    const interval = setInterval(() => {
      if (lastRefreshTime > 0) {
        const elapsed = Math.floor((Date.now() - lastRefreshTime) / 1000);
        const remaining = Math.max(0, COOLDOWN_SECONDS - elapsed);
        setCooldownRemaining(remaining);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [lastRefreshTime, COOLDOWN_SECONDS]);

  // Cycle to next song
  const cycleToNextSong = () => {
    if (top3Songs.length > 0) {
      const nextIndex = (currentIndex + 1) % top3Songs.length;
      setCurrentIndex(nextIndex);
      setOurSong(top3Songs[nextIndex]);
      localStorage.setItem('ourSongIndex', nextIndex.toString());
    }
  };

  // Refresh and find new songs
  const handleRefresh = () => {
    if (cooldownRemaining === 0) {
      findOurSong();
    }
  };

  if (myTracks.length === 0 || herTracks.length === 0) {
    return (
      <div className="bg-gradient-to-r from-pink-500/20 via-purple-500/20 to-green-500/20 p-8 rounded-2xl border border-pink-500/30 text-center">
        <div className="text-6xl mb-4">💕</div>
        <h3 className="text-3xl font-bold mb-4 bg-gradient-to-r from-pink-400 to-green-400 bg-clip-text text-transparent">
          Nuestra Canción
        </h3>
        <p className="text-gray-400">
          Necesitamos los datos musicales de ambos para encontrar su canción especial
        </p>
      </div>
    );
  }

  if (isCalculating) {
    return (
      <div className="bg-gradient-to-r from-pink-500/20 via-purple-500/20 to-green-500/20 p-8 rounded-2xl border border-pink-500/30 text-center">
        <div className="text-6xl mb-4">💕</div>
        <h3 className="text-3xl font-bold mb-4 bg-gradient-to-r from-pink-400 to-green-400 bg-clip-text text-transparent">
          Nuestra Canción
        </h3>
        <div className="flex items-center justify-center gap-3 mb-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-400"></div>
          <p className="text-gray-400">Analizando sus gustos musicales...</p>
        </div>
        <div className="text-sm text-gray-500">
          Comparando artistas, géneros y compatibilidad musical
        </div>
      </div>
    );
  }

  if (!ourSong) {
    return (
      <div className="bg-gradient-to-r from-pink-500/20 via-purple-500/20 to-green-500/20 p-8 rounded-2xl border border-pink-500/30 text-center">
        <div className="text-6xl mb-4">💕</div>
        <h3 className="text-3xl font-bold mb-4 bg-gradient-to-r from-pink-400 to-green-400 bg-clip-text text-transparent">
          Nuestra Canción
        </h3>
        <p className="text-gray-400">
          No se pudo encontrar una canción especial. ¡Exploren más música juntos!
        </p>
      </div>
    );
  }

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-r from-pink-500/20 via-purple-500/20 to-green-500/20 p-8 rounded-2xl border border-pink-500/30"
    >
      <div className="text-center mb-6">
        <div className="text-6xl mb-4">💕</div>
        <h3 className="text-3xl font-bold mb-4 bg-gradient-to-r from-pink-400 to-green-400 bg-clip-text text-transparent">
          Nuestra Canción
        </h3>
        <div className="flex items-center justify-center gap-2 mb-4">
          <div className="flex items-center gap-1">
            {[...Array(5)].map((_, i) => (
              <div 
                key={i} 
                className={`w-2 h-2 rounded-full ${
                  i < Math.floor(ourSong.compatibilityScore / 20) ? 'bg-pink-400' : 'bg-gray-600'
                }`} 
              />
            ))}
          </div>
          <span className="text-pink-400 font-bold">{ourSong.compatibilityScore}% Compatible</span>
        </div>
      </div>

      <div className="bg-white/10 p-6 rounded-xl max-w-md mx-auto">
        <div className="text-center">
          <motion.img 
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.3 }}
            src={ourSong.track.album.images[0]?.url || 'https://via.placeholder.com/200x200/FF69B4/white?text=♪'}
            alt={ourSong.track.name}
            className="w-32 h-32 mx-auto rounded-lg mb-4 shadow-2xl"
          />
          
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <h4 className="text-2xl font-bold text-white mb-2">
              {ourSong.track.name}
            </h4>
            <p className="text-gray-300 mb-3">
              {ourSong.track.artists.map((a: any) => a.name).join(', ')}
            </p>
            <p className="text-pink-400 text-sm font-semibold mb-4">
              {ourSong.reason}
            </p>
            
            <div className="space-y-3">
              <div className="text-xs text-gray-400">
                Álbum: {ourSong.track.album.name}
              </div>
              
              {/* Botón para reproducir en Spotify */}
              <a
                href={`https://open.spotify.com/track/${ourSong.track.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-full font-bold hover:bg-green-600 transition-all hover:scale-105"
              >
                🎵 Escuchar en Spotify
              </a>
            </div>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.7 }}
        className="text-center mt-6 space-y-3"
      >
        <div className="flex gap-3 justify-center items-center">
          <button
            onClick={cycleToNextSong}
            disabled={top3Songs.length <= 1}
            className="px-6 py-2 bg-white/10 text-white rounded-full hover:bg-white/20 transition-all text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            🔄 Siguiente ({currentIndex + 1}/{top3Songs.length})
          </button>

          <button
            onClick={handleRefresh}
            disabled={cooldownRemaining > 0}
            className="px-6 py-2 bg-pink-500/20 text-pink-400 border border-pink-500/50 rounded-full hover:bg-pink-500/30 transition-all text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {cooldownRemaining > 0
              ? `⏳ Espera ${cooldownRemaining}s`
              : '✨ Recalcular'}
          </button>
        </div>
        <p className="text-xs text-gray-500">
          "Recalcular" para buscar nuevas canciones
        </p>
      </motion.div>
    </motion.div>
  );
};